import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Plus, Eye, Search, GraduationCap } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  ativo: { label: 'Ativo', color: 'bg-green-100 text-green-800' },
  inativo: { label: 'Inativo', color: 'bg-gray-100 text-gray-800' },
  afastado: { label: 'Afastado', color: 'bg-yellow-100 text-yellow-800' },
  demitido: { label: 'Demitido', color: 'bg-red-100 text-red-800' },
};

const EstagiarioListPage = () => {
  const navigate = useNavigate();
  const [estagiarios, setEstagiarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadEstagiarios = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (status) params.status = status;
      if (activeOnly) params.active_only = true;
      const response = await axios.get(`${API}/api/estagiarios`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setEstagiarios(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao carregar estagiários');
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, status, activeOnly]);

  useEffect(() => {
    loadEstagiarios();
  }, [loadEstagiarios]);

  const getStatusConfig = (situacao) => {
    const key = (situacao || '').toLowerCase();
    return STATUS_CONFIG[key] || { label: situacao || 'N/A', color: 'bg-gray-100 text-gray-800' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const getPeriodo = (e) => {
    const perfil = e.perfil || {};
    const inicio = perfil.estagio_inicio || e.admissao;
    const fim = perfil.estagio_fim || e.data_demissao || '';
    if (!inicio && !fim) return '-';
    return `${formatDate(inicio)}${fim ? ` — ${formatDate(fim)}` : ''}`;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-blue-600" />
            Estagiários
          </h1>
          <p className="text-sm text-gray-500">
            Gerencie os colaboradores com contrato de estágio
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch id="active-only" checked={activeOnly} onCheckedChange={setActiveOnly} />
            <label htmlFor="active-only" className="text-sm text-gray-600 cursor-pointer">Somente ativos</label>
          </div>
          <Button onClick={() => navigate('/funcionarios/detalhado/novo?regime=ESTAGIARIO')}>
            <Plus className="h-4 w-4 mr-2" /> Novo Estagiário
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Buscar por nome, CPF ou matrícula..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={status || 'all'} onValueChange={(v) => setStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Todos os status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="Ativo">Ativo</SelectItem>
            <SelectItem value="Inativo">Inativo</SelectItem>
            <SelectItem value="Afastado">Afastado</SelectItem>
            <SelectItem value="Demitido">Demitido</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Período do Estágio</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Carregando...</TableCell></TableRow>
              ) : estagiarios.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Nenhum estagiário encontrado</TableCell></TableRow>
              ) : estagiarios.map(e => {
                const statusConf = getStatusConfig(e.situacao);
                return (
                  <TableRow
                    key={e.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => navigate(`/rh/estagiarios/${e.id}`)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {e.foto && e.foto !== 'default-avatar.png' ? (
                          <img src={e.foto} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold">
                            {(e.nome || '?').charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className="font-medium text-gray-900">{e.nome}</span>
                      </div>
                    </TableCell>
                    <TableCell>{e.matricula || '-'}</TableCell>
                    <TableCell>{e.cargo}</TableCell>
                    <TableCell>{e.departamento}</TableCell>
                    <TableCell>{getPeriodo(e)}</TableCell>
                    <TableCell><Badge className={statusConf.color}>{statusConf.label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(ev) => { ev.stopPropagation(); navigate(`/rh/estagiarios/${e.id}`); }}
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default EstagiarioListPage;
